import type { Ring, Vec2 } from '../../../geo/types';
import type { Tower } from '../../../data/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { boxSolid, horizontalCap, subFrame } from '../solids';
import { gableRoof } from '../roofSolids';
import { defensiveMasonryFace, TOWER_FOUNDATION_Y } from './towerMasonry';

// The dike entrance is built as a gate with a real passage in blokhuisGate.ts.
export const LASTAGE_BLOCKHOUSES = new Set(['tower-keeloff']);
export const LASTAGE_DEFENCE = { bodyScale: 0.94, footY: 0.4,
  keeloffFloorY: 5, keeloffRailY: 6, dikeEaveY: 12, dikeRoofRise: 4.8 } as const;

interface Styles { readonly wall: VertexStyle; readonly timber: VertexStyle; readonly roof: VertexStyle; }

function scaled(ring: Ring, factor: number): Ring {
  return ring.map(([u, v]): Vec2 => [u * factor, v * factor]);
}

/** A physical framing member along a plan edge, with its ends kept inside the reservation. */
function rail(
  builder: MeshBuilder, style: VertexStyle, frame: Frame, a: Vec2, b: Vec2,
  y0: number, y1: number, depth: number
): void {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const local = subFrame(frame, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2,
    Math.atan2(b[1] - a[1], b[0] - a[0]));
  boxSolid(builder, style, local, [-length / 2, y0, -depth / 2],
    [length / 2, y1, depth / 2], { top: true, bottom: true });
}

/** The Lastage fortifications were earth and Norwegian timber. The painting and woodcut
 * show Keeloff as a projecting open turret, and the documented wooden dike blockhouse as
 * a tall gabled tower. Their concealed frame sections, dimensions and openings are estimates. */
export function buildLastageBlockhouse(
  builder: MeshBuilder, frame: Frame, tower: Tower, ring: Ring, styles: Styles
): void {
  const keeloff = tower.id === 'tower-keeloff';
  const body = scaled(ring, LASTAGE_DEFENCE.bodyScale).map(([u, v]): Vec2 =>
    [keeloff ? Math.max(0.16, u) : u, v]);
  builder.prism(styles.wall, frame, ring, TOWER_FOUNDATION_Y, LASTAGE_DEFENCE.footY,
    { top: false, bottom: false, sides: true });
  horizontalCap(builder, styles.wall, frame, ring, LASTAGE_DEFENCE.footY, true);
  const eave = keeloff ? LASTAGE_DEFENCE.keeloffFloorY : LASTAGE_DEFENCE.dikeEaveY;
  body.forEach((a, i) => {
    const b = body[(i + 1) % body.length]!;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const plainBack = keeloff && i === body.length - 1;
    const windows = keeloff
      ? (plainBack || i % 2 === 0 ? [] : [{ x: width / 2 - 0.23, y: 2.6,
        width: 0.46, height: 0.6, cuts: [] }])
      : [2.6, 6.6].flatMap((y) => [1 / 3, 2 / 3].map((fraction) => ({
        x: width * fraction - 0.27, y, width: 0.54, height: 1.0, cuts: [],
      })));
    defensiveMasonryFace(builder, styles.timber, frame, a, b, LASTAGE_DEFENCE.footY,
      eave - LASTAGE_DEFENCE.footY, windows,
      { ...styles.timber, color: styles.timber.color.map((c) => c * 0.28) as [number, number, number] });
    const levels = keeloff ? [1.2, 3.6, 4.9, 5.85] : [0.65, 4.5, 8.5, 11.85];
    for (const y of levels) rail(builder, styles.timber, frame, a, b, y, y + 0.14, 0.16);
    // Separate structural posts remain legible from the water; they do not replace holes
    // in the boarded faces with painted dark marks.
    const postTop = keeloff ? LASTAGE_DEFENCE.keeloffRailY : eave;
    boxSolid(builder, styles.timber, frame,
      [a[0] - 0.07, LASTAGE_DEFENCE.footY, -a[1] - 0.07],
      [a[0] + 0.07, postTop, -a[1] + 0.07], { top: true, bottom: false });
  });
  if (keeloff) horizontalCap(builder, styles.timber, frame, body, eave, true);
  if (!keeloff) {
    const r = tower.radius * LASTAGE_DEFENCE.bodyScale;
    gableRoof(builder, { wall: styles.timber, roof: styles.roof }, frame,
      { u0: -r, u1: r, v0: -r, v1: r, eaveY: eave,
        ridgeY: eave + LASTAGE_DEFENCE.dikeRoofRise, ends: { start: true, end: true } });
  }
}
